'use client'

import { usePathname } from 'next/navigation'
import { ProductCardAnimationProvider } from 'product-card/context/ProductCardAnimationContext'
import { ProductCardAnimation } from 'product-card/types'
import { useProductListProvider } from 'product-list/context/ProductListProvider/useProductListProvider'
import { View } from 'product-list/types/View'
import { referrerFromProductPage } from 'product-list/utils/referrerFromProductPage'
import { referrerFromSameUrl } from 'product-list/utils/referrerFromSameUrl'
import { useMemo } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { isSessionStorageAvailable } from 'utils/webStorage/sessionStorage'

type Props = {
	isFirstProductCard: boolean
	children: React.ReactNode
}

export const AddProductCardAnimation = ({
	isFirstProductCard,
	children,
}: Props) => {
	const pathname = usePathname()
	const { storedState, view, shouldRenderSlideshowAnimation } =
		useProductListProvider()
	const { isLargeOrGreater } = useResponsive()

	const shouldRenderSlideshow = useMemo(() => {
		if (!shouldRenderSlideshowAnimation) {
			return false
		}
		const isServer = !isSessionStorageAvailable()
		const isFirstVisit =
			!(storedState && referrerFromProductPage(storedState.slotId)) &&
			!referrerFromSameUrl(pathname)
		const isSmallStandard = !isLargeOrGreater && view === View.Standard

		if (isServer || !isFirstVisit || !isSmallStandard) {
			return false
		}

		return isFirstProductCard
	}, [
		shouldRenderSlideshowAnimation,
		storedState,
		pathname,
		isLargeOrGreater,
		view,
		isFirstProductCard,
	])

	const animation = shouldRenderSlideshow
		? ProductCardAnimation.Slideshow
		: ProductCardAnimation.None

	return (
		<ProductCardAnimationProvider animation={animation}>
			{children}
		</ProductCardAnimationProvider>
	)
}
